<template>
    <div>
        <app-text
            label="Berufsbezeichnung"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.name'"
            :data-default="{ type: 'textpicker' }"
            :textpicker-namespace="['jobs/1/name', 'jobs/2/name', 'jobs/3/name']"
            required
            :maxlength="255"
        ></app-text>

        <app-wysiwyg
            label="Was wir suchen"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.tasks'"
            :data-default="{ type: 'textpicker' }"
            :textpicker-namespace="['jobs/1/tasks', 'jobs/2/tasks', 'jobs/3/tasks']"
            :maxlength="1024"
        ></app-wysiwyg>

        <app-wysiwyg
            label="Was wir bieten"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.offer'"
            :data-default="{ type: 'textpicker' }"
            :textpicker-namespace="['jobs/1/offer', 'jobs/2/offer', 'jobs/3/offer']"
            :maxlength="1024"
        ></app-wysiwyg>

        <app-wysiwyg
            label="Ihr Profil"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.profile'"
            :data-default="{ type: 'textpicker' }"
            :textpicker-namespace="['jobs/1/profile', 'jobs/2/profile', 'jobs/3/profile']"
            :maxlength="1024"
        ></app-wysiwyg>

        <app-select
            label="Art der Anstellung"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.type'"
            :options="['Festanstellung Vollzeit', 'Festanstellung Teilzeit', 'Praktikum']"
        ></app-select>

        <app-datepicker
            label="Einstellungsdatum"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.date'"
        ></app-datepicker>

        <app-datepicker
            label="Stellenanzeige aktiv ab"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.valid_from'"
        ></app-datepicker>

        <app-datepicker
            label="Stellenanzeige aktiv bis"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.valid_until'"
        ></app-datepicker>

        <app-text
            label="Standort"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.location'"
            :maxlength="255"
        ></app-text>

        <app-filepicker
            label="Bild"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.image'"
            :data-default="{ type: 'file' }"
            file-namespace="images/jobs/default"
            accept=".jpg,.png,.gif"
        ></app-filepicker>

        <app-text
            label="Ansprechpartner"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.contact_person'"
            :maxlength="255"
            :required="contactPersonRequired"
            revalidate-target="contact_forms"
        ></app-text>

        <app-checkboxes
            label="Bewerbung möglich als"
            :data-source="'deha.user.misc.jobs.' + listitemId + '.data.contact_forms'"
            :options="['E-Mail', 'Post', 'Telefon']"
            revalidate-source="contact_forms"
        >
        </app-checkboxes>

        <template v-if="contactForms.includes('E-Mail')">
            <app-text
                label="E-Mail-Adresse"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.email'"
                pattern="^\S+@\S+\.\S{2,8}$"
                :data-default="{ type: 'source', value: 'deha.user.email' }"
                :maxlength="255"
            ></app-text>
        </template>

        <template v-if="contactForms.includes('Telefon')">
            <app-text
                label="Telefonnummer"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.phone'"
                pattern="^(([+]{1,2}[0-9]{2,3})|[0]{1})[0-9]{0,10}[\(]{0,1}[0-9]{1,10}[\)]{0,1}[0-9]{1,10}[\s\|\/\|\-]{0,1}[0-9]{2,10}[\s\|\/\|\-]{0,1}[0-9]{0,10}$"
                autoformat="phone"
                :data-default="{ type: 'source', value: 'deha.user.telefon' }"
                :maxlength="255"
            ></app-text>
        </template>

        <template v-if="contactForms.includes('Post')">
            <app-text
                label="Firmenname"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.company'"
                :data-default="{ type: 'source', value: 'deha.user.firma1' }"
                :maxlength="255"
            ></app-text>

            <app-text
                label="Straße &amp; Hausnummer"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.address.street'"
                :data-default="{ type: 'source', value: 'deha.user.strasse' }"
                :maxlength="255"
            ></app-text>

            <app-text
                label="Postleitzahl"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.address.zip'"
                :data-default="{ type: 'source', value: 'deha.user.plz' }"
                pattern="^[0-9]{5,6}$"
                :maxlength="255"
            ></app-text>

            <app-text
                label="Ort"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.address.location'"
                :data-default="{ type: 'source', value: 'deha.user.ort' }"
                :maxlength="255"
            ></app-text>

            <app-select
                label="Land"
                :data-source="'deha.user.misc.jobs.' + listitemId + '.data.address.country'"
                :options="'Data.countries'"
                :data-default="{ type: 'source', value: 'deha.user.land' }"
            ></app-select>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { toArray } from '@/helpers/utils';
import Text from '@/components/fields/simple/Text.vue';
import Wysiwyg from '@/components/fields/simple/Wysiwyg.vue';
import Datepicker from '@/components/fields/simple/Datepicker.vue';
import Select from '@/components/fields/simple/Select.vue';
import Checkboxes from '@/components/fields/complex/Checkboxes.vue';
import Filepicker from '@/components/fields/complex/Filepicker.vue';

export default {
    data() {
        return {};
    },
    methods: {},
    computed: {
        ...mapGetters(['getProp']),
        contactForms() {
            let contact_forms = this.getProp(
                'deha.user.misc.jobs.' + this.listitemId + '.data.contact_forms'
            );
            if (contact_forms === undefined) {
                return [];
            }
            return toArray(contact_forms);
        },
        contactPersonRequired() {
            let contact_forms = this.getProp(
                'deha.user.misc.jobs.' + this.listitemId + '.data.contact_forms'
            );
            if (
                contact_forms === undefined ||
                contact_forms === null ||
                Object.keys(contact_forms).length === 0
            ) {
                return false;
            }
            return true;
        }
    },
    mounted() {
        this.$emit('controlhead', {
            title: this.$helpers.uiText('R017'),
            help: this.$helpers.uiText('R018'),
            removeText: this.$helpers.uiText('R019')
        });
    },
    props: {
        moduleId: String,
        listitemId: String
    },
    components: {
        'app-text': Text,
        'app-wysiwyg': Wysiwyg,
        'app-datepicker': Datepicker,
        'app-select': Select,
        'app-filepicker': Filepicker,
        'app-checkboxes': Checkboxes
    }
};
</script>
